import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  Assessment as AnalyticsIcon,
  WarningAmber as WarningAmberIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { vehicleService } from '../services';

const normalizeDateValue = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const formatDateOnly = (value?: string | null) => {
  const date = normalizeDateValue(value);
  if (!date) return value || '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(date);
};

const formatDateTime = (value?: string | null) => {
  const date = normalizeDateValue(value);
  if (!date) return value || '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(date);
};

const formatTat = (value?: string | null) => {
  if (!value) return '-';

  const matches = [...value.matchAll(/(\d+)\s*([dhms])/gi)];
  if (!matches.length) return value;

  const parts = matches
    .filter(([, amount]) => Number(amount) > 0)
    .map(([, amount, unit]) => `${amount}${unit.toLowerCase()}`);

  return parts.length ? parts.join(' ') : '0s';
};

const getDeploymentLink = (riderId?: string | null) => {
  let baseUrl = '';
  if (import.meta.env.VITE_NODE_ENV === 'production') {
   baseUrl = (import.meta.env.VITE_API_GATEWAY_URL ?? '').replace(/\/$/, '');
  }
  else {
    baseUrl = 'http://localhost:5173';
  }
  if (!riderId || !baseUrl) {
    return '';
  } 

  return `${baseUrl}/rider-management/${riderId}`;
};

interface DeploymentAnalytics {
  status: string;
  ticketCloseDate?: string;
  ticketCloseDateFrom: string;
  ticketCloseDateTo: string;
  city: Record<string, Array<{ reason: string; count: number }>>;
  total: number;
  returnCount: Record<string, number>;
}

const formatReason = (reason: string) =>
  reason.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const DEPLOYMENT_REASONS = ['NEW_RIDER', 'RE_JOINER', 'VEHICLE_EXCHANGE'];

const DeploymentTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<any[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [raisedDateFrom, setRaisedDateFrom] = useState('');
  const [raisedDateTo, setRaisedDateTo] = useState('');
  const [appliedRaisedDateFrom, setAppliedRaisedDateFrom] = useState('');
  const [appliedRaisedDateTo, setAppliedRaisedDateTo] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'pending' | 'completed' | 'rejected'>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [ticketToReject, setTicketToReject] = useState<string | null>(null);
  const [rejectingTicketId, setRejectingTicketId] = useState<string | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsFromDate, setAnalyticsFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [analyticsToDate, setAnalyticsToDate] = useState(new Date().toISOString().slice(0, 10));
  const [analyticsCity, setAnalyticsCity] = useState('all');
  const [analytics, setAnalytics] = useState<DeploymentAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    completed: 0,
    rejected: 0,
  });

  const fetchStatusCounts = useCallback(async (city: string = 'all') => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const query = city && city !== 'all' ? `?city=${encodeURIComponent(city)}` : '';
      const response = await fetch(`/api/v1/deployement-return-tickets/status-count${query}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch status counts with status ${response.status}`);
      }

      const payload = await response.json();
      const data = payload?.data ?? {};

      setStatusCounts({
        all: Number(data.all ?? 0),
        pending: Number(data.pending ?? 0),
        completed: Number(data.completed ?? 0),
        rejected: Number(data.rejected ?? 0),
      });
    } catch (err) {
      console.error('Failed to fetch deployment ticket status counts:', err);
      setStatusCounts({ all: 0, pending: 0, completed: 0, rejected: 0 });
    }
  }, []);

  const fetchDeploymentTickets = useCallback(async (pageNumber: number = currentPage, pageSize: number = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const statusQuery = selectedStatus !== 'all' ? `&status=${encodeURIComponent(selectedStatus)}` : '';
      const searchQuery = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const raisedDateFromQuery = appliedRaisedDateFrom ? `&dateFrom=${encodeURIComponent(appliedRaisedDateFrom)}` : '';
      const raisedDateToQuery = appliedRaisedDateTo ? `&dateTo=${encodeURIComponent(appliedRaisedDateTo)}` : '';
      const response = await fetch(`/api/v1/deployement-return-tickets/?page=${pageNumber}&limit=${pageSize}${statusQuery}${searchQuery}${raisedDateFromQuery}${raisedDateToQuery}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(
          errorPayload?.message || `Request failed with status ${response.status}`,
        );
      }

      const payload = await response.json();
      const result = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination ?? {};
      const nextTotalCount = Number(pagination.totalCount ?? result.length ?? 0);
      const nextTotalPages = Number(pagination.totalPages ?? Math.max(1, Math.ceil(nextTotalCount / pageSize)));

      setTickets(result);
      setFilteredTickets(result);
      setTotalCount(nextTotalCount);
      setTotalPages(nextTotalPages);
    } catch (err: any) {
      console.error('Failed to fetch deployment return tickets:', err);
      setError(err?.message || 'Failed to fetch deployment return tickets');
      setTickets([]);
      setFilteredTickets([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedRaisedDateFrom, appliedRaisedDateTo, currentPage, rowsPerPage, searchTerm, selectedStatus]);

  const fetchCityList = useCallback(async () => {
    try {
      const response = await vehicleService.getCities({ isActive: true });
      const cityList: string[] = Array.isArray(response?.data)
        ? response.data
            .map((city: any) => String(city?.name || city?.city || city?.cityName || '').trim())
            .filter((city): city is string => city.length > 0)
        : [];

      const uniqueCities = Array.from(new Set(cityList)).sort((a, b) => a.localeCompare(b));
      setCities(uniqueCities);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      setCities([]);
    }
  }, []);

  useEffect(() => {
    if (!tickets.length) {
      setFilteredTickets([]);
      return;
    }

    let nextList = tickets;

    if (selectedStatus !== 'all') {
      nextList = nextList.filter((ticket) => {
        return String(ticket?.status ?? '').trim().toLowerCase() === selectedStatus;
      });
    }

    if (selectedCity !== 'all') {
      const normalizedSelectedCity = String(selectedCity).trim().toLowerCase();
      nextList = nextList.filter((ticket) => {
        return String(ticket?.city ?? '').trim().toLowerCase() === normalizedSelectedCity;
      });
    }

    setFilteredTickets(nextList);
  }, [selectedStatus, selectedCity, tickets]);

  useEffect(() => {
    void fetchDeploymentTickets(currentPage, rowsPerPage);
    void fetchCityList();
    void fetchStatusCounts(selectedCity);
  }, [currentPage, fetchCityList, fetchStatusCounts, fetchDeploymentTickets, rowsPerPage, selectedCity]);

  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex = totalCount === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalCount);
  const showPagination = filteredTickets.length > 0;

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageNumberClick = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleRaisedDateFilterApply = () => {
    if (raisedDateFrom && raisedDateTo && raisedDateFrom > raisedDateTo) {
      setError('Ticket Raised On: From date cannot be after the To date.');
      return;
    }

    setError(null);
    setCurrentPage(1);
    setAppliedRaisedDateFrom(raisedDateFrom);
    setAppliedRaisedDateTo(raisedDateTo);
  };

  const handleRaisedDateFilterClear = () => {
    setRaisedDateFrom('');
    setRaisedDateTo('');
    setAppliedRaisedDateFrom('');
    setAppliedRaisedDateTo('');
    setError(null);
    setCurrentPage(1);
  };

  const fetchDeploymentAnalytics = async () => {
    if (!analyticsFromDate || !analyticsToDate) {
      setAnalyticsError('Please select both from and to dates.');
      return;
    }

    if (analyticsFromDate > analyticsToDate) {
      setAnalyticsError('From date cannot be after the to date.');
      return;
    }

    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const cityQuery = analyticsCity !== 'all' ? `&city=${encodeURIComponent(analyticsCity)}` : '';
      const response = await fetch(
        `/api/v1/deployement-return-tickets/completed-counts?ticketCloseDateFrom=${encodeURIComponent(analyticsFromDate)}&ticketCloseDateTo=${encodeURIComponent(analyticsToDate)}${cityQuery}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Request failed with status ${response.status}`);
      }

      setAnalytics(payload?.data ?? null);
    } catch (err: any) {
      console.error('Failed to fetch deployment analytics:', err);
      setAnalytics(null);
      setAnalyticsError(err?.message || 'Failed to fetch deployment analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleConfirmRejectTicket = async () => {
    if (!ticketToReject) {
      return;
    }

    const ticketId = ticketToReject;
    try {
      setRejectingTicketId(ticketId);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`/api/v1/deployement-return-tickets/${encodeURIComponent(ticketId)}/status`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'rejected' }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Request failed with status ${response.status}`);
      }

      await Promise.all([
        fetchDeploymentTickets(currentPage, rowsPerPage),
        fetchStatusCounts(selectedCity),
      ]);
      setTicketToReject(null);
      setSelectedTicket(null);
    } catch (err: any) {
      console.error('Failed to reject deployment ticket:', err);
      setError(err?.message || 'Failed to reject deployment ticket');
    } finally {
      setRejectingTicketId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Deployment Tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all deployment return ticket records.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/vehicles')}
          >
            Back to Inventory
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchDeploymentTickets()}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AnalyticsIcon />}
            onClick={() => setAnalyticsOpen(true)}
          >
            Analytics
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 360px', maxWidth: 560 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Search Tickets
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSearch();
            }}
            placeholder="Public Rider ID, phone, or Client Rider ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleSearch}>
                    Search
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 520px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Filter by Ticket Raised On
          </Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={raisedDateFrom}
                onChange={(event) => setRaisedDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                value={raisedDateTo}
                inputProps={{ min: raisedDateFrom || undefined }}
                onChange={(event) => setRaisedDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleRaisedDateFilterApply}>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleRaisedDateFilterClear}>
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <LocationOnIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Cities
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="All Cities"
            onClick={() => setSelectedCity('all')}
            color={selectedCity === 'all' ? 'primary' : 'default'}
            variant={selectedCity === 'all' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
          {cities.map((city) => (
            <Chip
              key={city}
              label={city}
              onClick={() => setSelectedCity(city)}
              color={selectedCity === city ? 'primary' : 'default'}
              variant={selectedCity === city ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'All', value: statusCounts.all, color: '#2563eb', bg: '#eff6ff', chip: 'info' },
          { label: 'Pending', value: statusCounts.pending, color: '#f59e0b', bg: '#fff7ed', chip: 'warning' },
          { label: 'Completed', value: statusCounts.completed, color: '#16a34a', bg: '#ecfdf5', chip: 'success' },
          { label: 'Rejected', value: statusCounts.rejected, color: '#dc2626', bg: '#fef2f2', chip: 'error' },
        ].map((item) => {
          const isActive =
            (item.label === 'All' && selectedStatus === 'all') ||
            (item.label.toLowerCase() === selectedStatus);

          return (
            <Grid item xs={12} sm={3} key={item.label}>
              <Card
                variant="outlined"
                onClick={() => {
                  const nextStatus = item.label === 'All' ? 'all' : item.label.toLowerCase() as 'pending' | 'completed' | 'rejected';
                  setSelectedStatus(nextStatus);
                }}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: item.color }}>
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ border: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Public ID(EV91 ID)</TableCell>
                    <TableCell>Rider Name</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source Name</TableCell>
                    <TableCell>Ticket Raised On</TableCell>
                    <TableCell>TAT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        {selectedStatus === 'all' && selectedCity === 'all'
                          ? 'No deployment tickets found.'
                          : selectedStatus !== 'all' && selectedCity === 'all'
                            ? `No ${selectedStatus} deployment tickets found.`
                            : selectedStatus === 'all' && selectedCity !== 'all'
                              ? `No deployment tickets found for ${selectedCity}.`
                              : `No ${selectedStatus} deployment tickets found for ${selectedCity}.`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((ticket) => {
                      return (
                        <TableRow
                          key={ticket.id}
                          hover
                          tabIndex={0}
                          role="button"
                          aria-label={`View deployment ticket for ${ticket.publicRiderId || ticket.phone || 'rider'}`}
                          onClick={() => setSelectedTicket(ticket)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedTicket(ticket);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: '-2px',
                            },
                          }}
                        >
                          <TableCell>{ticket.publicRiderId || '-'}</TableCell>
                          <TableCell>{ticket.rider?.name || '-'}</TableCell>
                          <TableCell>{ticket.clientName || '-'}</TableCell>
                          <TableCell>{ticket.phone || '-'}</TableCell>
                          <TableCell>{ticket.city || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={ticket.status || 'pending'}
                              size="small"
                              color={
                                ticket.status === 'pending'
                                  ? 'warning'
                                  : ticket.status === 'rejected'
                                    ? 'error'
                                    : 'success'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{ticket.referredById || '-'}</TableCell>
                          <TableCell>{formatDateTime(ticket.createdAt)}</TableCell>
                          <TableCell>{formatTat(ticket.totalTimeDifference)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 20px 60px rgba(15, 23, 42, 0.24)' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="overline" color="secondary.main" fontWeight={800} letterSpacing={1}>
            Completed ticket analytics
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            Close-date summary
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f8fafc' }}>
          <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={analyticsFromDate}
                onChange={(event) => setAnalyticsFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                value={analyticsToDate}
                inputProps={{ min: analyticsFromDate }}
                onChange={(event) => setAnalyticsToDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="deployment-analytics-city-label">City</InputLabel>
                <Select
                  labelId="deployment-analytics-city-label"
                  value={analyticsCity}
                  label="City"
                  onChange={(event) => setAnalyticsCity(event.target.value)}
                >
                  <MenuItem value="all">All cities</MenuItem>
                  {cities.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => void fetchDeploymentAnalytics()}
                disabled={analyticsLoading}
                sx={{ minHeight: 40, fontWeight: 700 }}
              >
                {analyticsLoading ? 'Loading...' : 'View'}
              </Button>
            </Grid>
          </Grid>

          {analyticsError && <Alert severity="error" sx={{ mb: 2 }}>{analyticsError}</Alert>}

          {analytics && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>Completed deployments</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Closed from {formatDateOnly(analytics.ticketCloseDateFrom)} to {formatDateOnly(analytics.ticketCloseDateTo)}{analyticsCity !== 'all' ? ` in ${analyticsCity}` : ''}
                  </Typography>
                </Box>
              </Box>
              {(() => {
                const responseCities = Object.keys(analytics.city || {});
                const returnCities = Object.keys(analytics.returnCount || {});
                const analyticsCities = analyticsCity !== 'all'
                  ? [analyticsCity]
                  : Array.from(new Set([...cities, ...responseCities, ...returnCities]));
                const countsByCity = analytics.city || {};
                const returnCountsByCity = analytics.returnCount || {};

                return analyticsCities.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>Reason</TableCell>
                        {analyticsCities.map((city) => (
                          <TableCell key={city} align="center" sx={{ fontWeight: 800, minWidth: 130 }}>
                            {city}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 800, minWidth: 110 }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DEPLOYMENT_REASONS.map((reason) => (
                        <TableRow key={reason} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{formatReason(reason)}</TableCell>
                          {analyticsCities.map((city) => {
                            const reasonCount = countsByCity[city]?.find((item) => item.reason === reason)?.count ?? 0;
                            return (
                              <TableCell key={`${city}-${reason}`} align="center">
                                <Chip label={reasonCount} size="small" color={reasonCount > 0 ? 'primary' : 'default'} sx={{ fontWeight: 800 }} />
                              </TableCell>
                            );
                          })}
                          <TableCell align="center">
                            <Chip
                              label={analyticsCities.reduce(
                                (total, city) => total + (countsByCity[city]?.find((item) => item.reason === reason)?.count ?? 0),
                                0,
                              )}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 700 }}>Return Tickets</TableCell>
                        {analyticsCities.map((city) => {
                          const returnCount = returnCountsByCity[city] ?? 0;
                          return (
                            <TableCell key={`return-${city}`} align="center">
                              <Chip label={returnCount} size="small" color={returnCount > 0 ? 'success' : 'default'} sx={{ fontWeight: 800 }} />
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          <Chip
                            label={analyticsCities.reduce((total, city) => total + (returnCountsByCity[city] ?? 0), 0)}
                            size="small"
                            color="success"
                            sx={{ fontWeight: 800 }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow
                        hover
                        sx={{
                          backgroundColor: '#fee0e0',
                          '&:hover': { backgroundColor: '#1d9fe6' },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 800, color: '#075985' }}>Net Deployment</TableCell>
                        {analyticsCities.map((city) => {
                          const newRiderCount = countsByCity[city]
                            ?.find((item) => item.reason === 'NEW_RIDER')?.count ?? 0;
                          const returnCount = returnCountsByCity[city] ?? 0;
                          const newDeploymentCount = newRiderCount - returnCount;
                          return (
                            <TableCell key={`new-deployment-${city}`} align="center">
                              <Chip
                                label={newDeploymentCount}
                                size="small"
                                color={newDeploymentCount > 0 ? 'info' : 'default'}
                                sx={{ fontWeight: 800, backgroundColor: '#bae6fd', color: '#075985' }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          <Chip
                            label={analyticsCities.reduce((total, city) => {
                              const newRiderCount = countsByCity[city]
                                ?.find((item) => item.reason === 'NEW_RIDER')?.count ?? 0;
                              return total + newRiderCount - (returnCountsByCity[city] ?? 0);
                            }, 0)}
                            size="small"
                              color="info"
                              sx={{ fontWeight: 800, backgroundColor: '#bae6fd', color: '#075985' }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{
                          backgroundColor: '#0f172a',
                          '&:hover': { backgroundColor: '#1e293b' },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Total</TableCell>
                        {analyticsCities.map((city) => (
                          <TableCell key={`total-${city}`} align="center">
                            <Chip
                              label={DEPLOYMENT_REASONS.reduce(
                                (total, reason) => total + (countsByCity[city]?.find((item) => item.reason === reason)?.count ?? 0),
                                0,
                              )}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 800, backgroundColor: '#dbeafe', color: '#1e3a8a' }}
                            />
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          <Chip
                            label={analytics.total}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 800, backgroundColor: '#dbeafe', color: '#1e3a8a' }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No cities are available for the selected analytics filter.</Alert>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setAnalyticsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selectedTicket !== null}
        onClose={() => setSelectedTicket(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.24)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography variant="overline" color="primary.main" fontWeight={800} letterSpacing={1}>
            Deployment ticket details
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {selectedTicket?.publicRiderId || selectedTicket?.riderId || 'Ticket'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f8fafc' }}>
          {selectedTicket && (
            <Grid container spacing={2}>
              {[
                ['Public Rider ID', selectedTicket.publicRiderId],
                ['Rider Name', selectedTicket.rider?.name],
                ['Internal Rider ID', selectedTicket.riderId],
                ['Client', selectedTicket.clientName],
                ['Client Rider ID', selectedTicket.clientRiderId],
                ['Phone', selectedTicket.phone],
                ['City', selectedTicket.city],
                ['Onboarded Date', formatDateOnly(selectedTicket.onboardedDate)],
                ['Source Name', selectedTicket.referredById],
                ['Ticket Raised By', selectedTicket.raisedBy],
                ['Ticket Raised On', formatDateTime(selectedTicket.createdAt)],
                ['Ticket Close Date', formatDateTime(selectedTicket.ticketCloseDate)],
                ['TAT', formatTat(selectedTicket.totalTimeDifference)],
                ['Rejected By', selectedTicket.rejectedBy],
                ['Paying Security Deposit', selectedTicket.payingSD == null ? '-' : `₹${Number(selectedTicket.payingSD).toLocaleString('en-IN')}`],
                ['Fixed Security Deposit', selectedTicket.fixedSecurityDeposit == null ? '-' : `₹${Number(selectedTicket.fixedSecurityDeposit).toLocaleString('en-IN')}`],
                ['Reason', selectedTicket.reason ? String(selectedTicket.reason).replace(/_/g, ' ') : '-'],
                ['Status', selectedTicket.status || 'pending'],
                ['Ticket ID', selectedTicket.id],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ height: '100%', p: 1.5, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                      {label}
                    </Typography>
                    {label === 'Status' ? (
                      <Chip
                        label={String(value)}
                        size="small"
                        color={selectedTicket.status === 'rejected' ? 'error' : selectedTicket.status === 'pending' ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                        {value || '-'}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => setSelectedTicket(null)}>
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedTicket && String(selectedTicket.status).toLowerCase() === 'pending' && (
              <>
                {getDeploymentLink(selectedTicket.riderId || selectedTicket.rider_id || selectedTicket.id) && (
                  <Button
                    component="a"
                    href={getDeploymentLink(selectedTicket.riderId || selectedTicket.rider_id || selectedTicket.id)}
                    target="_blank"
                    rel="noreferrer"
                    variant="contained"
                  >
                    Deploy vehicle
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setTicketToReject(selectedTicket.id);
                    setSelectedTicket(null);
                  }}
                  disabled={rejectingTicketId !== null}
                >
                  Reject ticket
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            p: 2,
            border: '2px solid #dbeafe',
            borderRadius: 2,
            backgroundColor: '#f8fbff',
            width: '100%',
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.08)',
            flexWrap: 'wrap',
            position: 'sticky',
            bottom: 0,
            zIndex: 50,
            left: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Showing {startIndex}-{endIndex} of {totalCount || filteredTickets.length}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="medium"
              disabled={currentPage === 1}
              onClick={handlePreviousPage}
              sx={{ minWidth: 100, fontWeight: 700 }}
            >
              Previous
            </Button>

            <Typography
              variant="body2"
              sx={{
                minWidth: 96,
                textAlign: 'center',
                color: 'text.secondary',
                fontWeight: 700,
              }}
            >
              Page {currentPage} of {totalPages}
            </Typography>

            <Button
              variant="contained"
              size="medium"
              disabled={currentPage >= totalPages}
              onClick={handleNextPage}
              sx={{ minWidth: 100, fontWeight: 700 }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={ticketToReject !== null}
        onClose={() => {
          if (!rejectingTicketId) {
            setTicketToReject(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.24)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700 }}>
          <WarningAmberIcon color="error" />
          Reject deployment ticket?
        </DialogTitle>
        <DialogContent sx={{ color: 'text.secondary', pt: 0.5 }}>
          This action will mark the ticket as rejected. Once rejected, it cannot be undone. Are you sure you want to reject this ticket?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setTicketToReject(null)}
            disabled={rejectingTicketId !== null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmRejectTicket()}
            disabled={rejectingTicketId !== null}
          >
            {rejectingTicketId !== null ? 'Rejecting...' : 'Yes, Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeploymentTicketsPage;
